import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TENANT_NAME_PATTERN,
  type TenantPartnerTransferResult,
} from '../../../../shared-types/src/index.js';
import { DataTable } from '../../components/DataTable.js';
import { FormField } from '../../components/FormField.js';
import type { Partner } from '../../api.js';
import type { Tenant } from '../../types.js';
import { owningPartnerLabel, partnerSelectOptions } from './tenant-partner.js';

/** Tenant provisioning plus explicit entry into the global top-bar context. */
export function TenantsTab({
  busy,
  tenants,
  partners,
  selectedTenantId,
  onCreate,
  onSelect,
  onDelete,
  onTransferPreview,
  onTransfer,
}: {
  busy: boolean;
  tenants: Tenant[];
  partners: Partner[];
  selectedTenantId: string;
  /** Creates the tenant and selects it; resolves true on success so the
   *  form knows to clear itself. */
  onCreate: (input: { name: string; partnerId?: string }) => Promise<boolean>;
  onSelect: (tenantId: string) => Promise<boolean>;
  onDelete: (tenant: Tenant) => Promise<void>;
  onTransferPreview: (
    tenant: Tenant,
    partnerId: string | null
  ) => Promise<TenantPartnerTransferResult | undefined>;
  onTransfer: (tenant: Tenant, partnerId: string | null) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const [newTenantName, setNewTenantName] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [transferTenant, setTransferTenant] = useState<Tenant>();
  const [transferPartnerId, setTransferPartnerId] = useState('');
  const [transferPreview, setTransferPreview] = useState<TenantPartnerTransferResult>();
  const trimmedName = newTenantName.trim();
  const tenantNameError =
    trimmedName && !TENANT_NAME_PATTERN.test(trimmedName) ? t('platform.tenantNameRule') : '';

  async function createTenant(event: FormEvent) {
    event.preventDefault();
    if (!trimmedName || tenantNameError) return;
    if (
      await onCreate({
        name: trimmedName,
        ...(partnerId ? { partnerId } : {}),
      })
    ) {
      setNewTenantName('');
      setPartnerId('');
    }
  }

  return (
    <div className="content-stack">
      <form className="panel form-grid" onSubmit={createTenant}>
        <h2>{t('platform.newTenant')}</h2>
        <FormField
          label={t('platform.name')}
          value={newTenantName}
          onChange={(event) => setNewTenantName(event.target.value)}
          placeholder="Voost AI Core"
          aria-invalid={Boolean(tenantNameError)}
          aria-describedby="tenant-name-rule"
        />
        <p
          id="tenant-name-rule"
          className={tenantNameError ? 'error' : 'field-help'}
          role={tenantNameError ? 'alert' : undefined}
        >
          {tenantNameError || t('platform.tenantNameRule')}
        </p>
        <label>
          <span>{t('platform.owningPartner')}</span>
          <select value={partnerId} onChange={(event) => setPartnerId(event.target.value)}>
            <option value="">{t('platform.owningPartnerDirect')}</option>
            {partnerSelectOptions(partners).map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.suspended
                  ? t('platform.partnerSuspendedOption', { name: option.name })
                  : option.name}
              </option>
            ))}
          </select>
        </label>
        <button disabled={!trimmedName || Boolean(tenantNameError) || busy}>
          {t('platform.createTenant')}
        </button>
      </form>

      <div className="panel">
        <h2>{t('platform.tenants')}</h2>
        <DataTable
          rows={tenants}
          columns={[
            {
              key: 'name',
              header: t('platform.name'),
              render: (tenant) => tenant.name,
            },
            {
              key: 'id',
              header: t('common.id'),
              render: (tenant) => tenant.id,
            },
            {
              key: 'partner',
              header: t('platform.owningPartner'),
              render: (tenant) => owningPartnerLabel(tenant, partners) ?? '—',
              sortValue: (tenant) => owningPartnerLabel(tenant, partners) ?? '',
            },
            {
              key: 'status',
              header: t('common.status'),
              render: (tenant) => tenant.status,
            },
            {
              key: 'actions',
              header: t('common.actions'),
              render: (tenant) => (
                <div className="row-actions">
                  <button
                    type="button"
                    className="secondary"
                    disabled={busy || selectedTenantId === tenant.id}
                    onClick={() => void onSelect(tenant.id)}
                  >
                    {t('partners.setTenantContext')}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    disabled={busy}
                    onClick={() => {
                      setTransferTenant(tenant);
                      setTransferPartnerId('');
                      setTransferPreview(undefined);
                    }}
                  >
                    {t('platform.changeOwnership')}
                  </button>
                  <button
                    type="button"
                    className="secondary danger"
                    disabled={busy}
                    onClick={() => void onDelete(tenant)}
                  >
                    {t('common.delete')}
                  </button>
                </div>
              ),
            },
          ]}
          empty={t('platform.noTenants')}
          getRowKey={(tenant) => tenant.id}
        />
      </div>

      {transferTenant ? (
        <section className="panel form-grid" aria-label={t('platform.changeOwnership')}>
          <h2>{t('platform.changeOwnership')}</h2>
          <p>
            {t('platform.transferTenantCurrent', {
              name: transferTenant.name,
              partner: owningPartnerLabel(transferTenant, partners) ?? t('platform.owningPartnerDirect'),
            })}
          </p>
          <label>
            <span>{t('platform.transferDestination')}</span>
            <select
              value={transferPartnerId}
              onChange={(event) => {
                setTransferPartnerId(event.target.value);
                setTransferPreview(undefined);
              }}
            >
              <option value="" disabled={!transferTenant.partnerId}>
                {t('platform.owningPartnerDirect')}
              </option>
              {partnerSelectOptions(partners).map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled || option.value === transferTenant.partnerId}
                >
                  {option.suspended
                    ? t('platform.partnerSuspendedOption', { name: option.name })
                    : option.name}
                </option>
              ))}
            </select>
          </label>
          <div className="row-actions">
            <button
              type="button"
              className="secondary"
              disabled={busy || transferPartnerId === (transferTenant.partnerId ?? '')}
              onClick={() =>
                void onTransferPreview(transferTenant, transferPartnerId || null).then((preview) =>
                  setTransferPreview(preview)
                )
              }
            >
              {t('platform.previewTransfer')}
            </button>
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() => {
                setTransferTenant(undefined);
                setTransferPreview(undefined);
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
          {transferPreview ? (
            <div className="callout">
              <strong>{t('platform.transferPreviewTitle')}</strong>
              <p>
                {t('platform.transferPreviewCredit', {
                  credits: transferPreview.credit.creditMillis ?? 0,
                  consumed: transferPreview.consumedMovedMillis,
                })}
              </p>
              <p>
                {t('platform.transferPreviewScopes', {
                  models: transferPreview.modelScopeAdded.length,
                  connectors: transferPreview.connectorScopeAdded.length,
                  sources: transferPreview.capabilitySourceScopeAdded.length,
                  customModels: transferPreview.customModelsUndeployed.length,
                })}
              </p>
              <button
                type="button"
                className="danger"
                disabled={busy}
                onClick={() =>
                  void onTransfer(transferTenant, transferPartnerId || null).then((changed) => {
                    if (!changed) return;
                    setTransferTenant(undefined);
                    setTransferPreview(undefined);
                  })
                }
              >
                {t('platform.executeTransfer')}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
